import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { 
  CCard, CCardHeader, CCardBody, CRow, CCol, CButton, 
  CButtonGroup, CFormInput, CTable, CTableBody, CTableRow, CTableDataCell, CBadge
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilMoon, cilSun, cilSettings, cilTrash, cilPlus, cilSave, cilPhone, cilWalk, cilCheckCircle, cilWarning } from '@coreui/icons';
import { formatDuration } from '../../utils/cameraUtils';
import { toast } from 'react-toastify';

const StoreShiftAnalyticsWidget = ({ store, storeAnalytics, onStoreUpdate }) => {
  const defaultShifts = [
    { id: 'day', name: 'Дневная смена', start: '09:00', end: '21:00', icon: cilSun },
    { id: 'night', name: 'Ночная смена', start: '21:00', end: '09:00', icon: cilMoon }
  ];

  const shifts = store.shifts_config && store.shifts_config.length > 0 ? store.shifts_config : defaultShifts;
  
  const [activeShiftId, setActiveShiftId] = useState(shifts[0]?.id);
  const [isEditing, setIsEditing] = useState(false);
  const [editableShifts, setEditableShifts] = useState(shifts);
  const [saving, setSaving] = useState(false);

  const getDurationInShift = (startIso, endIso, shiftStart, shiftEnd) => {
    const startMs = new Date(startIso).getTime();
    const endMs = new Date(endIso).getTime();
    const midMs = (startMs + endMs) / 2;
    
    const d = new Date(midMs);
    const m = d.getHours() * 60 + d.getMinutes();

    const [sh, sm] = shiftStart.split(':').map(Number);
    const [eh, em] = shiftEnd.split(':').map(Number);
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;

    const isOvernight = startMins > endMins;

    let isInShift = false;
    if (isOvernight) {
      isInShift = m >= startMins || m <= endMins;
    } else {
      isInShift = m >= startMins && m <= endMins;
    }

    return isInShift ? (endMs - startMs) / 1000 : 0;
  };

  // РАСЧЕТ АНАЛИТИКИ СМЕНЫ
  const shiftStats = useMemo(() => {
    const activeShift = shifts.find(s => s.id === activeShiftId);
    if (!activeShift || !storeAnalytics) return { downtime: 0, phone: 0, work: 0, unattendedFridge: 0 };

    let downtime = 0;
    let phone = 0;
    let work = 0;
    let unattendedFridge = 0;

    Object.values(storeAnalytics).forEach(cam => {
      // 1. Посты персонала
      if (cam.mode === 'personnel_control' || cam.mode === 'cafe') {
        cam.data?.general?.timeline?.forEach(seg => {
          const dur = getDurationInShift(seg.start, seg.end, activeShift.start, activeShift.end);
          if (dur > 0) {
            if (seg.class_id === -1) downtime += dur;
            else if (seg.people_count > 0) work += dur * seg.people_count;
          }
        });

        cam.data?.phone?.timeline?.forEach(seg => {
          const dur = getDurationInShift(seg.start, seg.end, activeShift.start, activeShift.end);
          if (dur > 0 && seg.phone_people_count > 0) {
            phone += dur * seg.phone_people_count;
          }
        });
      }

      // 2. Холодильник: Клиенты без внимания (>1 мин)
      if (cam.mode === 'fridge' || cam.data?.unattended_clients) {
        cam.data?.unattended_clients?.timeline?.forEach(seg => {
          const dur = getDurationInShift(seg.start, seg.end, activeShift.start, activeShift.end);
          if (dur > 0 && seg.class_id !== -120 && (seg.people_count > 0 || seg.unattended_client_count > 0)) {
            unattendedFridge += dur;
          }
        });
      }
    });

    return { downtime, phone, work, unattendedFridge };
  }, [activeShiftId, shifts, storeAnalytics]);

  const handleSaveShifts = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/stores/${store.id}/`, { shifts_config: editableShifts }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Настройки смен сохранены!');
      setIsEditing(false);
      onStoreUpdate({ ...store, shifts_config: editableShifts });
    } catch (e) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleAddShift = () => {
    setEditableShifts([...editableShifts, { id: Date.now().toString(), name: 'Новая смена', start: '00:00', end: '08:00' }]);
  };

  const updateShift = (id, field, value) => {
    setEditableShifts(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const activeShift = shifts.find(s => s.id === activeShiftId);
  const isNight = activeShift && parseInt(activeShift.start.split(':')[0]) > parseInt(activeShift.end.split(':')[0]);

  // Проверяем, есть ли в магазине холодильник
  const hasFridge = Object.values(storeAnalytics || {}).some(c => c.mode === 'fridge');

  return (
    <CCard className={`mb-4 border-top-3 shadow-sm ${isNight ? 'border-top-dark' : 'border-top-info'}`}>
      <CCardHeader className="bg-white d-flex justify-content-between align-items-center py-3 flex-wrap gap-3">
        <strong className="fs-5 d-flex align-items-center">
          <CIcon icon={isNight ? cilMoon : cilSun} size="xl" className={`me-2 ${isNight ? 'text-dark' : 'text-warning'}`} />
          Контроль смен: {activeShift?.name} ({activeShift?.start} - {activeShift?.end})
        </strong>
        
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {!isEditing ? (
            <>
              <CButtonGroup>
                {shifts.map(s => (
                  <CButton 
                    key={s.id}
                    color={activeShiftId === s.id ? (s.name.includes('Ноч') ? 'dark' : 'primary') : 'secondary'}
                    variant={activeShiftId === s.id ? '' : 'outline'}
                    onClick={() => setActiveShiftId(s.id)}
                    className="fw-bold"
                  >
                    {s.name}
                  </CButton>
                ))}
              </CButtonGroup>
              <CButton color="light" onClick={() => { setEditableShifts(shifts); setIsEditing(true); }}>
                <CIcon icon={cilSettings} />
              </CButton>
            </>
          ) : (
            <>
              <CButton color="secondary" onClick={() => setIsEditing(false)}>Отмена</CButton>
              <CButton color="success" className="text-white fw-bold" onClick={handleSaveShifts} disabled={saving}>
                <CIcon icon={cilSave} className="me-1" /> Сохранить
              </CButton>
            </>
          )}
        </div>
      </CCardHeader>

      <CCardBody className={isNight ? 'bg-light' : ''}>
        {isEditing ? (
          <div className="p-3 bg-white border rounded">
            <h6 className="fw-bold mb-3">Настройка расписания смен</h6>
            <CTable align="middle" responsive small>
              <CTableBody>
                {editableShifts.map((s, idx) => (
                  <CTableRow key={s.id}>
                    <CTableDataCell style={{ width: '40%' }}>
                      <CFormInput size="sm" value={s.name} onChange={e => updateShift(s.id, 'name', e.target.value)} placeholder="Название смены" />
                    </CTableDataCell>
                    <CTableDataCell style={{ width: '20%' }}>
                      <CFormInput size="sm" type="time" value={s.start} onChange={e => updateShift(s.id, 'start', e.target.value)} />
                    </CTableDataCell>
                    <CTableDataCell style={{ width: '20%' }}>
                      <CFormInput size="sm" type="time" value={s.end} onChange={e => updateShift(s.id, 'end', e.target.value)} />
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <CButton size="sm" color="danger" variant="ghost" onClick={() => setEditableShifts(prev => prev.filter(x => x.id !== s.id))}>
                        <CIcon icon={cilTrash} />
                      </CButton>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
            <CButton size="sm" color="primary" variant="outline" onClick={handleAddShift}><CIcon icon={cilPlus}/> Добавить смену</CButton>
          </div>
        ) : (
          <CRow className="text-center g-3">
            {/* АКТИВНАЯ РАБОТА */}
            <CCol md={hasFridge ? 3 : 4}>
              <div className="p-3 bg-white rounded border shadow-sm h-100">
                <CIcon icon={cilCheckCircle} size="xl" className="text-success mb-2" />
                <h6 className="text-muted text-uppercase fw-bold small mb-1">Время работы</h6>
                <span className="fs-4 fw-bold text-success">{formatDuration(shiftStats.work)}</span>
              </div>
            </CCol>

            {/* ПРОСТОЙ (ОТСУТСТВИЕ) */}
            <CCol md={hasFridge ? 3 : 4}>
              <div className="p-3 bg-white rounded border shadow-sm h-100 position-relative">
                <CIcon icon={cilWalk} size="xl" className={`${shiftStats.downtime > 900 ? 'text-danger' : 'text-warning'} mb-2`} />
                <h6 className="text-muted text-uppercase fw-bold small mb-1">Отсутствие на посту</h6>
                <span className={`fs-4 fw-bold ${shiftStats.downtime > 900 ? 'text-danger' : 'text-dark'}`}>
                  {formatDuration(shiftStats.downtime)}
                </span>
                {shiftStats.downtime > 1800 && (
                  <CBadge color="danger" className="position-absolute top-0 end-0 m-2">🚨 КРИТИЧНО</CBadge>
                )}
              </div>
            </CCol>

            {/* В ТЕЛЕФОНЕ */}
            <CCol md={hasFridge ? 3 : 4}>
              <div className="p-3 bg-white rounded border shadow-sm h-100 position-relative">
                <CIcon icon={cilPhone} size="xl" className={`${shiftStats.phone > 600 ? 'text-danger' : 'text-warning'} mb-2`} />
                <h6 className="text-muted text-uppercase fw-bold small mb-1">В телефоне</h6>
                <span className={`fs-4 fw-bold ${shiftStats.phone > 600 ? 'text-danger' : 'text-dark'}`}>
                  {formatDuration(shiftStats.phone)}
                </span>
                {shiftStats.phone > 1800 && (
                  <CBadge color="danger" className="position-absolute top-0 end-0 m-2">🚨 КРИТИЧНО</CBadge>
                )}
              </div>
            </CCol>

            {/* НОВАЯ 4-Я КОЛОНКА: КЛИЕНТЫ БЕЗ ВНИМАНИЯ В ХОЛОДИЛЬНИКЕ */}
            {hasFridge && (
              <CCol md={3}>
                <div className="p-3 bg-white rounded border shadow-sm h-100 position-relative">
                  <CIcon icon={cilWarning} size="xl" className={`${shiftStats.unattendedFridge > 300 ? 'text-danger' : 'text-warning'} mb-2`} />
                  <h6 className="text-muted text-uppercase fw-bold small mb-1">Холодильник без внимания</h6>
                  <span className={`fs-4 fw-bold ${shiftStats.unattendedFridge > 300 ? 'text-danger' : 'text-warning'}`}>
                    {formatDuration(shiftStats.unattendedFridge)}
                  </span>
                  {shiftStats.unattendedFridge > 600 && (
                    <CBadge color="danger" className="position-absolute top-0 end-0 m-2">🚨 КЛИЕНТЫ ЖДУТ</CBadge>
                  )}
                </div>
              </CCol>
            )}
          </CRow>
        )}
      </CCardBody>
    </CCard>
  );
};

export default StoreShiftAnalyticsWidget;